import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { Seo } from '@/components/Seo';

type LogoutPhase = 'loading' | 'confirm' | 'signing-out';

export function LogoutPage() {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<LogoutPhase>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading || phase === 'signing-out') return;

    if (isAuthenticated) {
      setPhase('confirm');
    }
  }, [isAuthenticated, isLoading, phase]);

  const handleConfirm = async () => {
    setPhase('signing-out');
    setError(null);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed. Please try again.');
      setPhase('confirm');
    }
  };

  if (!isLoading && !isAuthenticated && phase !== 'signing-out') {
    return <Navigate to="/login" replace />;
  }

  if (isLoading || phase === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#071a28]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2b82ea]" />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#071a28] px-4">
      <Seo title="Sign out — Payroll-GPT" path="/logout" noIndex />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(43,130,234,0.25), transparent 45%), radial-gradient(circle at 80% 80%, rgba(244,63,94,0.15), transparent 40%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 text-center shadow-2xl backdrop-blur-md">
        <BrandLogo className="mx-auto mb-6 h-12 w-auto max-w-[14rem]" />

        {phase === 'confirm' && (
          <>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-white">
              Sign out of Payroll-GPT?
            </h1>
            <p className="mt-2 text-sm text-white/65">
              You will need to sign in again to access your private AI workspace for payroll in
              Mauritius.
            </p>
            {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
            <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                variant="outline"
                className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                onClick={() => navigate('/')}
              >
                Cancel
              </Button>
              <Button
                className="bg-[#2b82ea] text-white hover:bg-[#3b8ff0]"
                onClick={handleConfirm}
              >
                Sign out
              </Button>
            </div>
          </>
        )}

        {phase === 'signing-out' && (
          <>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-white">
              Signing you out…
            </h1>
            <p className="mt-2 text-sm text-white/65">Clearing your secure session.</p>
            <Loader2 className="mx-auto mt-6 h-6 w-6 animate-spin text-[#2b82ea]" />
          </>
        )}
      </div>
    </div>
  );
}
